import find from 'lodash/find';
import each from 'lodash/each';
import get from 'lodash/get';
import { Validator } from 'vee-validate';

const ALPHA = new RegExp(/[a-z]/i);
const NUM = new RegExp(/[0-9]/);
const HEX = new RegExp(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
const GA = new RegExp(/^ua-\d{4,9}-\d{1,4}$/i);
const GA4 = new RegExp(/^G-\w{10,12}$/i);
const PHONE = new RegExp(/^\(\d{3}\)\s\d{3}-\d{4}/i);
const HAN = new RegExp(/[\u2E80-\u2E99\u2E9B-\u2EF3\u2F00-\u2FD5\u3005\u3007\u3021-\u3029\u3038-\u303B\u3400-\u4DB5\u4E00-\u9FCC\uF900-\uFA6D\uFA70-\uFAD9]|[\uD840-\uD868\uD86A-\uD86C][\uDC00-\uDFFF]|\uD86D[\uDC00-\uDF34\uDF40-\uDFFF]|\uD86E[\uDC00-\uDC1D]|\uD869[\uDC00-\uDED6\uDF00-\uDFFF]|\uD87E[\uDC00-\uDE1D]/);
const KATAKANA = new RegExp(/[\u30A1-\u30FA\u30FD-\u30FF\u31F0-\u31FF\u32D0-\u32FE\u3300-\u3357\uFF66-\uFF6F\uFF71-\uFF9D]|\uD82C\uDC00/);
const HIRAGANA = new RegExp(/[\u3041-\u3096\u309D-\u309F]|\uD82C\uDC01|\uD83C\uDE00/);
const HANGUL = new RegExp(/[\u1100-\u11FF\u302E\u302F\u3131-\u318E\u3200-\u321E\u3260-\u327E\uA960-\uA97C\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uFFA0-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]/);
const GREEK_COPTIC = new RegExp(/[Ͱ-Ͼἀ-῾]|\ud800[\udd40-\udd8e]|\ud834[\ude00-\ude4e]/);
const THAI = new RegExp(/[\u0E01-\u0E5B]/);
const NON_LATIN = [HAN, KATAKANA, HIRAGANA, HANGUL, GREEK_COPTIC, THAI];
const ZIP = new RegExp(/^\d{5}(?:[-\s]\d{4})?$/);
const POSTAL = new RegExp(/^[a-zA-Z0-9-_ ]+$/);
const DOMAIN = new RegExp(/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,10}\b([-a-zA-Z0-9@:%_\+.~#?&\/\/=]*)/);

Validator.extend('password', {
  getMessage: field => `The ${field} field must contain at least one number and at least one alphabetic character.`,
  validate: value => ALPHA.test(value) && NUM.test(value),
});

Validator.extend('hex', {
  getMessage: field => `The ${field} must be a valid hex color.`,
  validate: value => HEX.test(value),
});

Validator.extend('ga', {
  getMessage: field => 'The Google Analytics code must be valid.',
  validate: value => GA.test(value) || GA4.test(value),
});

Validator.extend('product_size', {
  getMessage: field => 'Choose a size.',
  validate: value => value ? true : false,
});

Validator.extend('phone', {
  getMessage: field => `The ${field} must be in the format (999) 999-9999.`,
  validate: value => PHONE.test(value),
});

Validator.extend('latin', {
  getMessage: field => 'Please use latin characters.',
  validate: value => !find(NON_LATIN, LANG => LANG.test(value)),
});

Validator.extend('zip_code', {
  getMessage: field => 'Please input a valid zip code.',
  validate: value => ZIP.test(value),
});

Validator.extend('postal_code', {
  getMessage: field => 'Please input a valid postal code.',
  validate: value => POSTAL.test(value),
});

Validator.extend('domain', {
  getMessage: field => 'Please input a valid domain.',
  validate: value => DOMAIN.test(value),
});


export default class ValidatorHelper {
  constructor(rules) {
    this.rules = rules;

    this.validator = new Validator();

    for (let key in rules) {
      this.validator.attach({ name: key, rules: rules[key].rules, alias: rules[key].pretty });
    }
  }

  hasRule(keyName) {
    return !!find(this.rules, (rule, key) => key === keyName);
  }

  validateAll(obj) {
    let values = {};
    each(this.rules, (rule, key) => values[key] = obj[key]);
    return this.validator.validateAll(values)
  }

  validate(field, value) {
    return this.validator.validate(field, value);
  }

  updateRule(name, rules, alias) {
    let field = this.validator.fields.find({ name });

    field.update({ rules, alias });
  }

  hasErrors() {
    return this.validator.errors.any();
  }

  hasError(field) {
    return this.validator.errors.has(field);
  }

  firstError(field) {
    return field ?
      this.validator.errors.first(field) :
      get(this.validator.errors, 'errors[0].msg');
  }

  addError(field, message) {
    return this.validator.errors.add(field, message);
  }

  removeError(field) {
    return this.validator.errors.remove(field);
  }

  clear() {
    return this.validator.errors.clear();
  }

  attach(name, rules, alias) {
    return this.validator.attach({ name, rules, alias });
  }

  detach(keyName) {
    return this.validator.detach(keyName);
  }
};

export let tagValidator = new ValidatorHelper({
  tags: {
    rules: 'max_value:3',
    pretty: 'tags',
  },
  tag: {
    rules: 'min:5|max:15',
    pretty: 'tag',
  },
  hasTag: {
    rules: 'required',
  },
  duplicateTags: {},
});